import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  KeyRound,
  Plus,
  RefreshCw,
  TriangleAlert
} from 'lucide-react'
import type { SshKeyDTO } from '@shared/types'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  IconButton,
  Skeleton,
  useToast
} from '@/components/ui'
import PageHeader from '@/components/layout/PageHeader'
import AddKeyDialog from './AddKeyDialog'
import KeyCard from './KeyCard'

interface GuideStep {
  title: string
  command?: string
  body?: ReactNode
  note?: ReactNode
}

const GUIDE_STEPS: GuideStep[] = [
  {
    title: 'Check whether you already have an SSH key',
    command: 'ls ~/.ssh/*.pub',
    note: 'If you see a .pub file — id_ed25519.pub, id_rsa.pub — you already have a key pair. Skip to step 3.'
  },
  {
    title: 'Generate a key pair (only if you have none)',
    command: 'ssh-keygen -t ed25519',
    note: 'Press Enter at every prompt to accept the defaults. A passphrase is optional; if you set one, Argus will ask for it below.'
  },
  {
    title: 'Copy the public half of the key',
    command: 'cat ~/.ssh/id_ed25519.pub',
    note: 'If your key is id_rsa, use that filename instead. The public half is the only part WP Engine needs.'
  },
  {
    title: 'Add the public key in the WP Engine portal',
    body: (
      <>
        Open the <span className="font-medium text-fg">WP Engine User Portal</span> →{' '}
        <span className="font-medium text-fg">SSH Keys</span> (under your profile, top right), paste
        the public key and save.
      </>
    ),
    note: 'The key applies to every install your account can reach, so you only do this once.'
  },
  {
    title: 'Add the private key here, then connect a site',
    command: 'cat ~/.ssh/id_ed25519',
    note: (
      <>
        Paste that text into <span className="font-medium text-fg">Add key</span> — treat it like a
        password. Then connect a site from the Sites page: Argus tests the SSH connection before it
        saves anything. Use the install name (<span className="font-mono">tinytowncampgr</span>), not
        the domain (tinytowncampground.com).
      </>
    )
  }
]

const SKELETON_IDS = ['k1', 'k2']

function CommandRow({ command }: { command: string }): React.ReactElement {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    []
  )

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      if (timer.current !== null) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setCopied(false), 1800)
    } catch {
      toast({
        title: 'Could not copy',
        description: 'Your browser blocked clipboard access — select the command and copy it manually.',
        variant: 'error'
      })
    }
  }

  return (
    <div className="mt-2 flex items-center gap-2 rounded-control border border-line bg-sunken py-1.5 pr-1.5 pl-2.5">
      <code className="min-w-0 flex-1 overflow-x-auto font-mono text-[11px] whitespace-pre text-fg">
        {command}
      </code>
      <IconButton label={`Copy command: ${command}`} size="sm" onClick={() => void copy()}>
        {copied ? <Check className="text-ok-600 dark:text-ok-300" /> : <Copy />}
      </IconButton>
      <span aria-live="polite" className="sr-only">
        {copied ? 'Command copied to clipboard' : ''}
      </span>
    </div>
  )
}

function SetupGuide(): React.ReactElement {
  return (
    <ol className="space-y-5">
      {GUIDE_STEPS.map((step, index) => (
        <li key={step.title} className="flex gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent"
          >
            {index + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg">{step.title}</p>
            {step.body ? (
              <p className="mt-1 text-sm leading-relaxed text-fg-muted">{step.body}</p>
            ) : null}
            {step.command ? <CommandRow command={step.command} /> : null}
            {step.note ? (
              <p className="mt-1.5 text-xs leading-relaxed text-fg-subtle">{step.note}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  )
}

export default function KeysPage(): React.ReactElement {
  const { toast } = useToast()
  const [keys, setKeys] = useState<SshKeyDTO[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  // `null` means "follow the key list" — the guide is what an empty page is for.
  const [guideOpen, setGuideOpen] = useState<boolean | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      setKeys(await api.get<SshKeyDTO[]>('/ssh-keys'))
      setError(null)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const hasKeys = keys.length > 0
  const showGuide = guideOpen ?? !hasKeys

  return (
    <>
      <PageHeader
        title="SSH keys"
        description="Argus reaches your WP Engine installs over SSH. Keys are encrypted before they are stored and are shared by every site you connect."
        actions={
          <>
            <Button
              variant="secondary"
              iconLeft={<RefreshCw />}
              loading={loading && hasKeys}
              onClick={() => void load()}
            >
              Refresh
            </Button>
            <Button iconLeft={<Plus />} onClick={() => setAddOpen(true)}>
              Add key
            </Button>
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2.5 rounded-card border border-bad-500/40 bg-bad-100/70 px-4 py-3 text-sm text-bad-700 dark:bg-bad-950/60 dark:text-bad-300"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="space-y-6">
        {loading && !hasKeys ? (
          <div aria-busy="true" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <span className="sr-only">Loading your SSH keys…</span>
            {SKELETON_IDS.map((id) => (
              <Card key={id}>
                <CardBody className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="size-10 shrink-0" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-40" />
                </CardBody>
              </Card>
            ))}
          </div>
        ) : hasKeys ? (
          <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {keys.map((sshKey) => (
              <li key={sshKey.id} className="flex">
                <KeyCard
                  sshKey={sshKey}
                  onDeleted={(id) => setKeys((current) => current.filter((k) => k.id !== id))}
                  onDesignationChanged={() => void load()}
                />
              </li>
            ))}
          </ul>
        ) : (
          <Card>
            <EmptyState
              icon={<KeyRound />}
              title="No SSH keys yet"
              description="An SSH key is how Argus proves to WP Engine that it may read and update your media library — the same key you would use for wp-cli over SSH. Add your private key once and every site you connect can use it."
              action={
                <Button iconLeft={<Plus />} onClick={() => setAddOpen(true)}>
                  Add your first key
                </Button>
              }
            />
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1.5">
                <CardTitle>Set up SSH access to WP Engine</CardTitle>
                <CardDescription>
                  Five steps, once per machine. You will end up pasting a private key into Argus and
                  a public key into the WP Engine portal.
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                aria-expanded={showGuide}
                aria-controls="ssh-setup-guide"
                iconRight={showGuide ? <ChevronUp /> : <ChevronDown />}
                onClick={() => setGuideOpen(!showGuide)}
              >
                {showGuide ? 'Hide' : 'Show'}
              </Button>
            </div>
          </CardHeader>
          <CardBody id="ssh-setup-guide" className={cn(!showGuide && 'hidden')}>
            <SetupGuide />
          </CardBody>
        </Card>
      </div>

      <AddKeyDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(created) => {
          setKeys((current) => [created, ...current.filter((k) => k.id !== created.id)])
          toast({
            title: 'SSH key added',
            description: `${created.label} is ready to use.`,
            variant: 'success'
          })
        }}
      />
    </>
  )
}

export { KeysPage }
